'use client'
import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { BookOpenText, ChevronDown, ClipboardList, LayoutDashboardIcon, SettingsIcon } from 'lucide-react'
import { cn } from '@workspace/ui/lib/utils'
import { Subject } from '@/shared/lib/authorization'
import { defineAbilityFor } from '@/shared/lib/authorization/ability'
import { ADMIN_ROLE_NAME } from '@/shared/constants'
import { selectUser } from '@/shared/store/auth.store'
import { useIsMobile } from '@workspace/ui/hooks/use-mobile'

interface MenuItem {
    title: string
    href: string
    icon?: React.ReactElement
    children?: MenuItem[]
    subject?: Subject
    isMobile?: boolean
}

interface MenuGroup {
    title?: string
    items: MenuItem[]
}

const menuGroups: MenuGroup[] = [
    {
        items: [
            {
                title: 'Tổng quan',
                href: '/dashboard',
                icon: <LayoutDashboardIcon size={16} strokeWidth={1.5} />,
            },
            {
                title: 'Danh mục dự án',
                href: '/projects',
                icon: <ClipboardList size={16} strokeWidth={1.5} />,
                subject: 'project',
            },
            {
                title: 'Danh mục pháp lý',
                href: '/legal-documents',
                icon: <LayoutDashboardIcon size={16} strokeWidth={1.5} />,
                children: [
                    {
                        title: 'Danh mục pháp lý (Công việc)',
                        href: '/legal-documents/tasks',
                    },
                    {
                        title: 'Phê duyệt',
                        href: '/legal-documents/approvals',
                    },
                ],
                subject: 'task',
            },
            {
                title: 'Tài liệu tham khảo',
                href: '/documents',
                icon: <BookOpenText size={16} strokeWidth={1.5} />,
                subject: 'library',
            },
            {
                title: 'Quản trị hệ thống',
                href: '/system',
                icon: <SettingsIcon size={16} strokeWidth={1.5} />,
                children: [
                    {
                        title: 'Người dùng & Phân quyền',
                        href: '/users',
                    },
                    {
                        title: 'Mẫu biểu nội bộ',
                        href: '/templates',
                    },
                    {
                        title: 'Dữ liệu hệ thống',
                        href: '/categories/departments',
                    },
                    {
                        title: 'Cấu hình phê duyệt',
                        href: '/approvals',
                    },
                    {
                        title: 'Nhật ký hệ thống',
                        href: '/logs',
                    },
                ],
                subject: 'system',
            },
        ],
    },
]

export function SidebarMenu() {
    const user = selectUser()
    const isMobile = useIsMobile({ breakpoint: 1024 })

    // Recompute menu items whenever user (and permissions) change
    const menuGroupsWithPermission = useMemo(() => {
        const canView = (item: MenuItem) => {
            if (!item.subject) return true
            if (!user) return false
            if (user.role?.name === ADMIN_ROLE_NAME) return true
            if (user.permissions && user.permissions.length > 0) {
                const ability = defineAbilityFor(user.permissions)
                return ability.can('view', item.subject) || ability.can('operate', item.subject)
            }
            if (user.role?.permissions && user.role.permissions.length > 0) {
                const ability = defineAbilityFor(user.role.permissions)
                return ability.can('view', item.subject) || ability.can('operate', item.subject)
            }
            return false
        }

        return menuGroups
            .map(group => ({
                ...group,
                items: group.items.filter(canView),
            }))
            .filter(group => group.items.length > 0)
    }, [user])

    return (
        <nav className={cn('flex', isMobile ? 'flex-col' : 'items-center gap-1 border-b')}>
            {menuGroupsWithPermission.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                    {group.items.map(item => (
                        <MenuItem {...item} key={item.title} isMobile={isMobile} />
                    ))}
                </React.Fragment>
            ))}
        </nav>
    )
}

function MenuItem({ title, href, icon, children, isMobile }: MenuItem) {
    const router = useRouterState()
    const pathname = router.location.pathname
    const [isExpanded, setIsExpanded] = useState(false)
    const menuRef = useRef<HTMLDivElement>(null)

    // Check if current path or any child path is active
    const isActive =
        pathname === href ||
        pathname.startsWith(href) ||
        (children && children.some(child => pathname.startsWith(child.href)))
    const hasActiveChild = children?.some(child => pathname === child.href)

    const hasChildren = children && children.length > 0

    // Toggle dropdown on click for better accessibility
    const handleToggle = () => {
        if (hasChildren) {
            setIsExpanded(!isExpanded)
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsExpanded(false)
            }
        }

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isExpanded])

    return (
        <div
            ref={menuRef}
            className={cn('relative', isMobile && 'w-full')}
            onMouseEnter={() => !isMobile && hasChildren && setIsExpanded(true)}
        >
            {/* Main menu item */}
            {hasChildren ? (
                <button
                    onClick={handleToggle}
                    className={cn(
                        'flex items-center gap-2 text-sm px-3 py-2 transition-colors whitespace-nowrap cursor-pointer',
                        'text-foreground border-b-2 border-transparent hover:border-accent focus-visible:outline-none focus-visible:border-accent',
                        (isActive || hasActiveChild) && 'border-primary! font-semibold',
                        isMobile && 'w-full border-b-0 rounded-md hover:bg-accent',
                        isMobile && (isActive || hasActiveChild) && 'bg-accent border-primary! text-accent-foreground',
                    )}
                >
                    {icon}
                    <span>{title}</span>
                    <ChevronDown
                        size={14}
                        strokeWidth={1.5}
                        className={cn('transition-transform duration-200', isExpanded && 'rotate-180')}
                    />
                </button>
            ) : (
                <Link
                    preload={false}
                    to={href}
                    className={cn(
                        'flex items-center gap-2 text-sm px-3 py-2 transition-colors whitespace-nowrap',
                        'text-foreground border-b-2 border-transparent hover:border-accent focus-visible:outline-none focus-visible:border-accent',
                        isActive && 'border-primary! font-semibold',
                        isMobile && 'w-full border-b-0 rounded-md hover:bg-accent',
                        isMobile && isActive && 'bg-accent border-primary! text-accent-foreground',
                    )}
                >
                    {icon}
                    <span>{title}</span>
                </Link>
            )}

            {/* Dropdown menu */}
            {hasChildren && isExpanded && (
                <div
                    className={cn(
                        isMobile ? 'relative ml-4 mt-1 border-l pl-2' : 'absolute top-full left-0 min-w-60 pt-2 z-999',
                    )}
                >
                    <div
                        className={cn(
                            !isMobile && 'bg-background border rounded-lg shadow-lg z-50 py-1',
                            isMobile && 'flex flex-col gap-1',
                        )}
                    >
                        {children.map(child => {
                            const isChildActive = pathname.startsWith(child.href)
                            return (
                                <Link
                                    key={child.title}
                                    to={child.href}
                                    className={cn(
                                        'flex items-center gap-2 text-sm px-4 py-2 transition-colors',
                                        'text-foreground hover:bg-accent focus-visible:outline-none',
                                        isChildActive && 'bg-primary! text-zinc-950',
                                    )}
                                    onClick={() => setIsExpanded(false)}
                                    preload={false}
                                >
                                    <span>{child.title}</span>
                                </Link>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}
